/**
 * @file StatementStepSetup.js
 * @version 2.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-20
 * @modified 2026-10-20
 *
 * Generate Statements, step 1 — Set up: what kind of run (month-end or
 * interim, Cam 20 Oct 2026), the month, and the customers. Two cards: the run
 * on the left, the customers on the right with their own scrolling list.
 * The checks (review flags, Xero sync, excluded contacts) are chips in the
 * frame, not panels here.
 *
 * 2.0.0 (2026-10-20, Cam — the statements look): cards with one title each,
 * option cards for the kind, the month as a segmented control, a 40px
 * customer list (account number muted, "+N linked" and a solid "Excluded"
 * badge with its reason), and an empty state that says what to do.
 */

import React from 'react';
import DateInput from './DateInput';
import SearchableDropdown from './SearchableDropdown';
import { RUN_KIND_LABEL, RUN_KIND_HELP } from '../utils/statementRunKind';

/**
 * @param {object} props
 * @param {'month_end'|'interim'} props.runKind
 * @param {function} props.onRunKind
 * @param {string|null} props.kindReadyReason - why Interim cannot be chosen at all (migration), or null
 * @param {string|null} props.kindBlock - why these dates do not suit the chosen kind, or null
 * @param {{start, end}} props.dates
 * @param {function} props.onDate - (field, value)
 * @param {Array<{ label, range, active }>} props.presets
 * @param {function} props.onPreset
 * @param {Array} props.selectedContacts
 * @param {Array} props.contactGroups
 * @param {Array} props.allCustomerContacts
 * @param {string} props.contactDropdownValue
 * @param {boolean} props.loadingGroup
 * @param {function} props.onAddGroup
 * @param {function} props.onAddContact
 * @param {function} props.onRemoveContact
 * @param {function} props.onViewContact
 * @param {boolean} [props.generated] - a run has been generated (changing anything here starts it again)
 */
function StatementStepSetup({
  runKind, onRunKind, kindReadyReason, kindBlock,
  dates, onDate, presets, onPreset,
  selectedContacts, contactGroups, allCustomerContacts, contactDropdownValue, loadingGroup,
  onAddGroup, onAddContact, onRemoveContact, onViewContact, generated
}) {
  const kinds = ['month_end', 'interim'];
  return (
    <div className="sr-setup">
      <section className="sr-panel sr-setup-run">
        <div className="sr-card-head">
          <div>
            <h4 className="sr-card-title">The run</h4>
            <p className="sr-card-sub">What kind of statements, and for which month</p>
          </div>
        </div>
        <div className="sr-card-body">
          <div className="sr-section">
            <span className="sr-label">Kind of run</span>
            <p className="sr-help">{generated ? 'Changing the kind, month or customers generates the statements again.' : 'Month-end for everyone; interim to re-issue a month after it went out.'}</p>
            <div className="sr-options" role="radiogroup" aria-label="Kind of run">
              {kinds.map(k => {
                const disabled = k === 'interim' && !!kindReadyReason;
                return (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={runKind === k}
                    className={`sr-option${runKind === k ? ' on' : ''}`}
                    onClick={() => !disabled && onRunKind(k)}
                    disabled={disabled}
                    title={disabled ? kindReadyReason : undefined}
                  >
                    <span className="sr-radio" aria-hidden="true" />
                    <span>
                      <span className="sr-option-title">
                        <span className="material-symbols-rounded">{k === 'interim' ? 'history_edu' : 'calendar_month'}</span>
                        {RUN_KIND_LABEL[k]}
                      </span>
                      <span className="sr-option-text">{disabled ? kindReadyReason : RUN_KIND_HELP[k]}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="sr-section">
            <span className="sr-label">Month</span>
            <p className="sr-help">{runKind === 'interim' ? 'Interim runs re-issue a month that has ended.' : 'Statements cover the whole month.'}</p>
            <div className="sr-seg" role="group" aria-label="Month">
              {presets.map(p => (
                <button key={p.label} type="button" className={p.active ? 'on' : ''} aria-pressed={!!p.active} onClick={() => onPreset(p.range)}>
                  {p.label}
                </button>
              ))}
            </div>
            <div className="sr-period-dates">
              <DateInput label="From" value={dates.start} onChange={(e) => onDate('start', e.target.value)} />
              <DateInput label="To" value={dates.end} onChange={(e) => onDate('end', e.target.value)} />
            </div>
            {runKind === 'interim' && (
              <p className={`sr-hint${kindBlock ? ' bad' : ''}`}>
                <span className="material-symbols-rounded">{kindBlock ? 'error' : 'info'}</span>
                {kindBlock || `Shown as at ${dates.end ? new Date(`${dates.end}T12:00:00Z`).toLocaleDateString('en-GB', { timeZone: 'UTC' }) : 'the month end'}, then what has changed since.`}
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="sr-panel sr-setup-customers">
        <div className="sr-card-head">
          <div>
            <h4 className="sr-card-title">Customers <span className="sr-count">{selectedContacts.length}</span></h4>
            <p className="sr-card-sub">Add a contact group, or customers one by one</p>
          </div>
        </div>
        <div className="sr-customer-pickers">
          <SearchableDropdown
            options={allCustomerContacts
              .filter(c => !selectedContacts.find(s => s.id === c.id))
              .map(c => ({
                value: c.id,
                badge: c.account_no || ' ',
                label: c.company_name || ' ',
                searchText: `${c.account_no || ''} ${c.company_name || ''}`
              }))}
            value={contactDropdownValue}
            onChange={onAddContact}
            placeholder="Add a customer by name or account…"
            searchPlaceholder="Type to search…"
            renderActions={(option) => (
              <>
                <button className="btn-icon" style={{ background: 'var(--bg-th)', borderRadius: '50%' }} title="View contact" onClick={() => onViewContact(option.value)}>
                  <span className="material-symbols-rounded">visibility</span>
                </button>
                <button className="btn-icon" style={{ background: 'var(--bg-th)', borderRadius: '50%' }} title="Add contact" onClick={() => onAddContact(option.value)}>
                  <span className="material-symbols-rounded">add</span>
                </button>
              </>
            )}
          />
          <SearchableDropdown
            options={contactGroups.map(group => ({
              value: group.name, label: group.name, badge: String(group.contactCount), searchText: group.name
            }))}
            value=""
            onChange={(groupName) => onAddGroup(groupName)}
            placeholder={loadingGroup ? 'Loading group…' : 'Add a contact group…'}
            searchPlaceholder="Search groups…"
            disabled={loadingGroup}
          />
        </div>
        <div className="sr-card-body flush">
          {selectedContacts.length === 0 ? (
            <div className="sr-empty">
              <span className="sr-empty-icon"><span className="material-symbols-rounded">group_add</span></span>
              <h5>No customers yet</h5>
              <p>Add a contact group for a month-end run, or search for the customers an interim run is for.</p>
            </div>
          ) : (
            <div className="sr-list">
              {selectedContacts.map(contact => {
                const isStatementChild = !!contact.parent_account_id && contact.link_statements === true;
                const statementChildren = parseInt(contact.statement_children_count || 0, 10);
                return (
                  <div key={contact.id} className={`sr-list-row${isStatementChild ? ' excluded' : ''}`}>
                    <span className="sr-acc">{contact.account_no || '—'}</span>
                    <span className="sr-name">
                      {contact.company_name}
                      {isStatementChild && (
                        <span className="sr-sub">
                          Child of <strong>{contact.parent_company_name || 'a parent account'}</strong>
                          {contact.parent_account_no ? ` (${contact.parent_account_no})` : ''} — its statements roll up to the
                          parent, so this account will be <strong>excluded</strong>. Select the parent instead.
                        </span>
                      )}
                    </span>
                    <span>
                      {statementChildren > 0 && !isStatementChild && (
                        <span className="sr-badge soft neutral" title={`Includes ${statementChildren} linked child account${statementChildren !== 1 ? 's' : ''} in this statement.`}>
                          <span className="material-symbols-rounded">account_tree</span>+{statementChildren} linked
                        </span>
                      )}
                      {isStatementChild && (
                        <span className="sr-badge solid warning">
                          <span className="material-symbols-rounded">block</span>Excluded
                        </span>
                      )}
                    </span>
                    <button onClick={() => onRemoveContact(contact.id)} className="btn-icon" title="Remove" aria-label={`Remove ${contact.company_name}`}>
                      <span className="material-symbols-rounded">close</span>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default StatementStepSetup;
